/**
 * Client for the staff-user directory (`/users`) and the tenant's roles
 * (`/roles`).
 *
 * A "directory" here is just `/users` filtered by a permission — Persons →
 * Drivers is `?permission=delivery.deliver`. The matching `/roles?permission=`
 * filter answers the other half: which roles would put a NEW user into that
 * directory. Both use the same server-side whereExists, so the add-form can
 * only offer roles that really do land the person in the list.
 */
import type { DriverProfile, RoleCreate, User, UserCreate, UserCreated, UserWithRoles } from '@erp/shared';

import { api } from './api.js';

export type { DriverProfile, UserCreate, UserCreated, UserWithRoles } from '@erp/shared';

export interface RoleOption {
  id: string;
  code: string;
  name: string;
}

/**
 * Why a user cannot sign in, computed server-side.
 *
 * Login resolves an email to a tenant through the PLATFORM `user_email_lookup`
 * table, which lives in a DIFFERENT database from the tenant's `users` row on a
 * split tenant — so the two halves can drift, and nothing in the UI used to show
 * it. The server compares them (set membership in JS, not a join) and reports:
 *  • `no_lookup`      — no platform row at all; nothing can route to this user.
 *  • `email_mismatch` — the platform row points at a different address.
 *  • `invited`        — the row is `invited`; login only accepts `active`.
 *
 * "No password" is deliberately absent: `password_hash` is NOT NULL, so every
 * user has SOME hash. What is detectable is whether anyone can route to them.
 */
export type SignInIssue = 'no_lookup' | 'email_mismatch' | 'invited';

/** A `/users` row. `signInIssue` is absent/null when the user can sign in. */
export interface UserListItem extends UserWithRoles {
  signInIssue?: SignInIssue | null;
}

/**
 * `GET /users/:id`. `roleIds` is what the edit dialog prefills its role picker
 * from — the LIST only carries role NAMES, which are editable and non-unique, so
 * they can't be matched back to ids. It stays optional because a server that
 * predates the addition simply omits it; the dialog then leaves roles untouched
 * rather than silently clearing them.
 */
export interface UserDetail extends User {
  roleIds?: string[];
  /** Warehouses this person may WRITE against. Empty/absent = unrestricted. */
  warehouseIds?: string[];
}

/**
 * Fields `PATCH /users/:id` accepts. Wider than `@erp/shared`'s `UserUpdate`:
 * `email` is the sign-in identity, so changing it has to move the platform
 * lookup row in step — see the endpoint. `status` deliberately omits `invited`
 * (a dead, un-loggable state that only exists because `MandobService` sets it).
 */
export interface UserPatch {
  fullName?: string;
  email?: string;
  phone?: string | null;
  locale?: 'en' | 'ar';
  status?: 'active' | 'disabled';
  roleIds?: string[];
  /** Replaces the whole set. `[]` clears the restriction (= every warehouse). */
  warehouseIds?: string[];
}

/** `permission` filters to users whose roles grant that permission code. */
export async function listUsers(permission?: string): Promise<UserListItem[]> {
  const r = await api().get<{ items: UserListItem[] }>('/users', {
    params: permission ? { permission } : undefined,
  });
  return r.data.items;
}

/** One user with their role ids — the edit dialog's prefill. */
export async function getUser(id: string): Promise<UserDetail> {
  return (await api().get<UserDetail>(`/users/${id}`)).data;
}

/** Update identity, language, status and/or roles. */
export async function updateUser(id: string, input: UserPatch): Promise<UserWithRoles> {
  return (await api().patch<UserWithRoles>(`/users/${id}`, input)).data;
}

/**
 * Set someone else's password. Omit `password` and the server generates one.
 * The plaintext comes back exactly ONCE (never stored or logged) and the target's
 * sessions are revoked, so an old refresh token can't outlive the change.
 */
export async function setUserPassword(
  id: string,
  password?: string,
): Promise<{ email: string; password: string }> {
  return (await api().post<{ email: string; password: string }>(`/users/${id}/password`, password ? { password } : {})).data;
}

/**
 * Repair a broken sign-in: re-point the platform `user_email_lookup` row at this
 * user's current email (and activate an `invited` row). 409 `users.email_taken_globally`
 * means another tenant genuinely owns the address — that needs a human decision.
 */
export async function repairUserLogin(id: string): Promise<void> {
  await api().post(`/users/${id}/repair-login`);
}

/** Soft-delete (`deleted_at`). History keeps resolving — documents store user ids. */
export async function deleteUser(id: string): Promise<void> {
  await api().delete(`/users/${id}`);
}

/**
 * Create a user. The response carries `tempPassword` ONLY when the server
 * generated one (no password supplied) — it is the single chance to read it.
 */
export async function createUser(input: UserCreate): Promise<UserCreated> {
  return (await api().post<UserCreated>('/users', input)).data;
}

export async function setUserStatus(
  id: string,
  status: 'active' | 'disabled',
): Promise<UserWithRoles> {
  return (await api().patch<UserWithRoles>(`/users/${id}`, { status })).data;
}

/** `permission` filters to roles that GRANT that permission code. */
export async function listRoles(permission?: string): Promise<RoleOption[]> {
  const r = await api().get<{ items: RoleOption[] }>('/roles', {
    params: permission ? { permission } : undefined,
  });
  return r.data.items;
}

/** Find-or-create a role with the given grants (idempotent on `code`). */
export async function createRole(input: RoleCreate): Promise<RoleOption> {
  return (await api().post<RoleOption>('/roles', input)).data;
}

/** Aggregated driver profile — deliveries, counters and proof photos. */
export async function getDriverProfile(userId: string): Promise<DriverProfile> {
  return (await api().get<DriverProfile>(`/users/${userId}/driver-profile`)).data;
}
